import { ComponentProps, memo, ReactNode } from 'react';
import * as Sentry from '@sentry/nextjs';

import { GlobalStyles, NextAppHead } from '@common/components';

import { RootContextProvider } from '../root-context-provider/RootContextProvider';

type RootContextProviderProps = ComponentProps<typeof RootContextProvider>;

type NextAppHeadProps = ComponentProps<typeof NextAppHead>;

type SentryErrorBoundaryProps = ComponentProps<typeof Sentry.ErrorBoundary>;

interface Props extends RootContextProviderProps {
  /**
   * NextAppHead용 SEO 설정
   * next-seo 라이브러리의 NextSeo 컴포넌트에 전달됩니다
   */
  seo?: NextAppHeadProps['seo'];
  /**
   * viewport meta tag 설정
   * @default 'width=device-width,initial-scale=1.0,minimum-scale=1.0,maximum-scale=1.0,user-scalable=no,viewport-fit=cover'
   */
  viewport?: NextAppHeadProps['viewport'];
  /**
   * Sentry ErrorBoundary의 fallback UI
   * React.ReactElement 또는 에러 정보를 받아 ReactElement를 반환하는 함수를 지정합니다
   */
  fallback?: SentryErrorBoundaryProps['fallback'];
  children?: ReactNode;
}

/**
 * Next.js 앱 전용 Root Context Provider입니다.
 *
 * 다음 기능을 통합하여 제공합니다:
 * - Sentry.ErrorBoundary (@sentry/nextjs)
 * - RootContextProvider (AppConfig, UserAgent, Tracker, Theme, BrandStyle, QueryClient)
 * - NextAppHead (viewport, preconnect, font preload, SEO)
 * - GlobalStyles (CSS reset, 폰트)
 *
 * @example
 * #### 기본적인 사용
 * ```tsx
 * // pages/_app.tsx
 * import { NextRootContextProvider } from '@common/contexts';
 *
 * function MyApp({ Component, pageProps }: AppProps) {
 *   return (
 *     <NextRootContextProvider userAgent={pageProps.user_agent}>
 *       <Component {...pageProps} />
 *     </NextRootContextProvider>
 *   );
 * }
 * ```
 *
 * @example
 * #### SEO와 앱 설정 포함
 * ```tsx
 * // pages/_app.tsx
 * import { NextRootContextProvider } from '@common/contexts';
 *
 * function MyApp({ Component, pageProps }: AppProps) {
 *   return (
 *     <NextRootContextProvider
 *       userAgent={pageProps.user_agent}
 *       seo={pageProps.seo}
 *       appConfig={{
 *         zigzagUuid: pageProps.zigzagUuid,
 *         isZigzagApp: pageProps.isZigzagApp,
 *       }}
 *     >
 *       <Component {...pageProps} />
 *     </NextRootContextProvider>
 *   );
 * }
 * ```
 *
 * @example
 * #### 상품카드 컨텍스트와 함께 사용
 * ```tsx
 * // pages/_app.tsx
 * import { NextRootContextProvider } from '@common/contexts';
 * import { LikeSubject, useLikeObserve } from '@widgets/like-product';
 * import { ProductCardProvider } from '@widgets/product-card';
 *
 * function MyApp({ Component, pageProps }: AppProps) {
 *   return (
 *     <NextRootContextProvider userAgent={pageProps.user_agent} seo={pageProps.seo}>
 *       <LikeSubject>
 *         <ProductCardProvider useLikeObserver={useLikeObserve}>
 *           <Component {...pageProps} />
 *         </ProductCardProvider>
 *       </LikeSubject>
 *     </NextRootContextProvider>
 *   );
 * }
 * ```
 */
export const NextRootContextProvider = memo(({ seo, viewport, fallback, children, ...rootContextProps }: Props) => {
  return (
    <Sentry.ErrorBoundary fallback={fallback}>
      <RootContextProvider {...rootContextProps}>
        <NextAppHead seo={seo} viewport={viewport} />
        <GlobalStyles />
        {children}
      </RootContextProvider>
    </Sentry.ErrorBoundary>
  );
});
